import React, { useCallback, useMemo } from "react";
import * as TableRenderers from "../shared/renderers/table";
import { FormConfiguration } from "../shared/crud/form";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { translationFieldConfig } from "../shared/widgets/translation";
import originalSchema from "../../gen/schema/witbooking.backoffice.occupation.v1/Occupation.json";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { defineMessage } from "react-intl";
import { getLanguageFromFullLocale } from "../../lib/config";
import actions from "../../lib/actions";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SECTION } from "../../lib/constants";
import {
  generateOccupationTicker,
  generateOccupationTranslation,
  occupationMessages,
  tableHeaderMessages,
} from "./messages";
import { IChangeEvent } from "react-jsonschema-form";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { v4 as uuidv4 } from "uuid";
import { TableColumns } from "../shared/crud/list";
import { OccupationEntity } from "./domain/occupation.entity";
import { useProperty } from "../../components/property-provider";
import {
  createOccupation,
  deleteOccupation,
  getAllOccupations,
  getOccupation,
  updateOccupation,
} from "./infrastructure/occupation.api";

const schema = {
  ...originalSchema,
  properties: {
    ...originalSchema.properties,
    seniors: {
      ...originalSchema.properties.seniors,
      minimum: 0,
    },
    adults: {
      ...originalSchema.properties.adults,
      minimum: 1,
    },
    juniors: {
      ...originalSchema.properties.juniors,
      minimum: 0,
    },
    children: {
      ...originalSchema.properties.children,
      minimum: 0,
    },
    babies: {
      ...originalSchema.properties.babies,
      minimum: 0,
    },
    autoGenerateTranslations: {
      type: "boolean",
    },
  },
};

const messages = {
  ...defaultCrudMessages,
  ...occupationMessages,
  patternValidationFailed: {
    ".ticker": defineMessage({
      id: "occupation.tickerPatternFailed",
      defaultMessage: "Must be uppercase. Doesn't allow blank spaces or special characters",
    }),
  },
  tableHeaders: tableHeaderMessages,
};

interface OccupationFilters {
  ticker: string;
}
function buildFormConfiguration(allowedLocales: string[]): FormConfiguration<OccupationEntity> {
  return {
    __fieldOrder: [
      "ticker",
      "seniors",
      "adults",
      "juniors",
      "children",
      "babies",
      "autoGenerateTranslations",
      "translationsList",
      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    ticker: {
      readonly: true,
      messages: {
        label: tableHeaderMessages.ticker,
        helpText: messages.occupationTickerHelpText,
      },
    },
    seniors: {
      default: () => 0,
      readonly: isNew => !isNew,
    },
    adults: {
      default: () => 2,
      readonly: isNew => !isNew,
    },
    juniors: {
      default: () => 0,
      readonly: isNew => !isNew,
    },
    children: {
      default: () => 0,
      readonly: isNew => !isNew,
    },
    babies: {
      default: () => 0,
      readonly: isNew => !isNew,
    },
    inUse: {
      hidden: true,
    },
    autoGenerateTranslations: {
      default: () => true,
      readonly: isNew => !isNew,
      widget: SwitchGroup,
      messages: {
        label: defineMessage({
          id: "occupation.autoGenerateTranslations.label",
          defaultMessage: "Auto-generate translations fields?",
        }),
      },
    },
    translationsList: {
      ...translationFieldConfig(allowedLocales || []),
      children: {
        __fieldOrder: ["name", "description", "*"],
        name: {
          messages: {
            label: messages.occupationTranslationNameLabel,
          },
        },
        description: { hidden: true },
        ...translationFieldConfig(allowedLocales || []).children,
      },
      messages: {
        label: messages.occupationTranslationsTitle,
      },
    },
  };
}
function buildTableColumns(): TableColumns<OccupationEntity> {
  return {
    ticker: {
      renderer: TableRenderers.StringRenderer,
      width: 140,
    },
    translationsList: {
      renderer: TableRenderers.mapRenderer(TableRenderers.StringRenderer, (v, intl) => {
        if (!v || v.length === 0) return undefined;
        const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
        return current?.name || v[0].name;
      }),
    },
    seniors: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    adults: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    juniors: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    children: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    babies: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
  };
}
function OccupationCrud({ propertyTicker }: { propertyTicker: string }) {
  const isEditing = useMemo(() => window.location.pathname.includes("/edit"), [
    window?.location.pathname,
  ]);
  const allowedLocales = usePropertyLocales();
  const propertyInfo = useProperty();

  const onGetAll = useCallback(async () => getAllOccupations(propertyInfo.token), [
    propertyInfo.token,
  ]);
  const onDelete = useCallback(async (id: string) => deleteOccupation(id, propertyInfo.token), [
    propertyInfo.token,
  ]);
  const onGetOne = useCallback(async id => getOccupation(id, propertyInfo.token), [
    propertyInfo.token,
  ]);
  const onCreate: (entity: OccupationEntity) => Promise<string> = useCallback(
    async entity => createOccupation(entity, propertyInfo.token),
    [propertyInfo.token]
  );
  const onUpdate: (entity: OccupationEntity) => Promise<void> = useCallback(
    async entity => updateOccupation(entity, propertyInfo.token),
    [propertyInfo.token]
  );

  const disableDelete = useCallback(
    (value: OccupationEntity) => value.inUse && "occupation.inUse",
    []
  );
  const handleOnChange = useCallback(
    (e: IChangeEvent<OccupationEntity>) => {
      if (isEditing) {
        return e.formData;
      }
      const newData = { ...e.formData };
      newData.ticker = generateOccupationTicker(e.formData);
      if (newData.autoGenerateTranslations) {
        newData.translationsList = generateOccupationTranslation(e.formData, allowedLocales);
      }
      return newData;
    },
    [allowedLocales, isEditing]
  );
  const tableColumns = useMemo(() => buildTableColumns(), []);
  const formConfiguration = useMemo(() => buildFormConfiguration(allowedLocales), [allowedLocales]);
  if (allowedLocales === undefined) {
    return null;
  }

  return (
    <SimpleCrud<OccupationEntity, OccupationFilters>
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      messages={messages}
      onGetAll={onGetAll}
      onGetOne={onGetOne}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      canCreate={actions.CreateOccupation}
      canUpdate={actions.ModifyOccupation}
      disableDelete={disableDelete}
      section={SECTION.OCCUPATIONS}
      onChange={handleOnChange}
      listHeight={"100%"}
    />
  );
}

export default withAuthorizationVerifier(OccupationCrud);
